import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, Landmark, Megaphone, Vote, Store, Leaf, GraduationCap, BarChart3, MapPin, Users, Network, TrendingUp, Target, ArrowRight, CheckCircle2, Calendar, ClipboardList, Globe2, ShieldCheck, Layers, Activity } from 'lucide-react';
import { useT } from '../components/site/i18n';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 24
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-283" data-mp="1dvayixo99yar">
    {children}
  </motion.div>;
const MODULE_ICONS = [Megaphone, Vote, Store, Leaf, GraduationCap, BarChart3];
const USE_CASE_ICONS = [Users, TrendingUp, Calendar, ClipboardList, Leaf, Store, GraduationCap, Vote];
const SDG_COLORS = ['#A21942', '#DD1367', '#FD9D24', '#BF8B2E', '#19486A'];
const ECOSYSTEM_ICONS = [Landmark, Building2, Leaf, GraduationCap, Store, BarChart3];
export function BairrouGov() {
  const t = useT();
  return <div className="min-h-screen bg-white dark:bg-gray-900 pt-24 relative overflow-hidden transition-colors duration-300" data-id="element-284" data-mp="bgdvqkn17rdh">
      {/* ===== Hero ===== */}
      <section className="relative bg-brand-charcoal dark:bg-gray-950 text-white px-6 pt-20 pb-28 overflow-hidden" data-id="element-285" data-mp="lcqq7kqei99a">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)',
        backgroundSize: '48px 48px'
      }} aria-hidden="true" data-id="element-286" data-mp="qbtuh2yk4bg7"></div>
        <div className="max-w-7xl mx-auto relative z-10 grid lg:grid-cols-2 gap-16 items-center" data-id="element-287" data-mp="2w6v5055lj4uv">
          <FadeIn data-id="element-288" data-mp="3cegf5h0ivzr">
            <span className="inline-flex items-center gap-2 text-brand-green-light font-bold tracking-wider uppercase text-xs mb-6 bg-white/5 border border-white/10 px-4 py-2 rounded-full" data-id="element-289" data-mp="3uh4huxkupnmd">
              <Landmark size={14} aria-hidden="true" data-id="element-290" data-mp="89o34dd0otjw" />
              {t.gov.heroEyebrow}
            </span>
            <h1 className="text-4xl md:text-6xl font-sans font-extrabold mb-8 leading-[1.1] tracking-tight" data-id="element-291" data-mp="m95xr9czv116">
              {t.gov.heroTitle1}{' '}
              <span className="text-brand-green-light" data-id="element-292" data-mp="h9ta4meatpxj">{t.gov.heroTitle2}</span>
            </h1>
            <p className="text-lg text-white/70 mb-10 leading-relaxed max-w-xl" data-id="element-293" data-mp="vypfucsvuquz">
              {t.gov.heroDesc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-294" data-mp="r28prd24i2f9">
              <Link to="/contato" className="inline-flex items-center justify-center gap-2 bg-brand-green hover:bg-brand-green-dark text-white px-8 py-4 rounded-lg text-base font-bold transition-all shadow-sm hover:shadow-md group" data-id="element-295" data-mp="150oxn7bq1yyz">
                {t.gov.ctaPrimary}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" data-id="element-296" data-mp="1okxl50sh43ai" />
              </Link>
              <Link to="/contato" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 px-8 py-4 rounded-lg text-base font-bold transition-all" data-id="element-297" data-mp="1elpzlqbl52cb">
                {t.gov.ctaSecondary}
              </Link>
            </div>
          </FadeIn>

          {/* Hero dashboard mock */}
          <FadeIn delay={0.2} className="relative" data-id="element-298" data-mp="vxb70ylmdg9e">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 border border-gray-200 dark:border-gray-700" data-id="element-299" data-mp="hwqp04nj3ogy">
              <div className="flex items-center justify-between mb-6" data-id="element-300" data-mp="mv1yge9wvwyn">
                <div className="flex items-center gap-2 text-brand-charcoal dark:text-white font-bold" data-id="element-301" data-mp="ew73hv7wdjcn">
                  <Activity size={18} className="text-brand-green" aria-hidden="true" data-id="element-302" data-mp="9wax5pbq9ftx" />
                  {t.gov.dashboardPanel}
                </div>
                <span className="text-xs font-bold text-brand-green-dark dark:text-brand-green-light bg-brand-green/10 px-3 py-1 rounded-full" data-id="element-303" data-mp="13r4r6m147hj">
                  {t.gov.dashboardLive}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-5" data-id="element-304" data-mp="3ndtpvn50pb0n">
                {[0, 0, 0].map((v, i) => <div key={i} className="bg-brand-cream dark:bg-gray-900 rounded-xl p-4 text-center" data-id="element-305" data-mp="3ie7ap85mpkp7" data-mpi={i}>
                    <div className="text-2xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-306" data-mp="1gkhh7n3ee474">
                      {v}
                    </div>
                    <div className="h-1.5 w-full bg-brand-green/20 rounded-full mt-2" data-id="element-307" data-mp="2uhaph7brzthb">
                      <div className="h-full w-0 bg-brand-green rounded-full" data-id="element-308" data-mp="2zg5it37dd0fo"></div>
                    </div>
                  </div>)}
              </div>
              <div className="bg-brand-charcoal dark:bg-gray-950 rounded-xl p-4 relative overflow-hidden h-32" data-id="element-309" data-mp="347nk5sdb1316">
                <MapPin className="text-brand-green-light absolute top-4 left-8" size={20} aria-hidden="true" data-id="element-310" data-mp="38aju6uos3qti" />
                <MapPin className="text-brand-yellow absolute top-12 right-12" size={16} aria-hidden="true" data-id="element-311" data-mp="2ght5r6dc76et" />
                <MapPin className="text-brand-green-light absolute bottom-5 left-1/3" size={18} aria-hidden="true" data-id="element-312" data-mp="2pj0x14hfr6ze" />
                <div className="absolute inset-0 opacity-20" style={{
                backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
                backgroundSize: '24px 24px'
              }} aria-hidden="true" data-id="element-313" data-mp="4qxn4im1y8z5"></div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ===== What is BairrouGov ===== */}
      <section className="py-24 px-6 max-w-7xl mx-auto" data-id="element-314" data-mp="1i55flh2tl8km">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-315" data-mp="1d5kjrfiy5gc4">
          <FadeIn data-id="element-316" data-mp="1d37tmf31bznz">
            <h2 className="text-3xl md:text-4xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-317" data-mp="1ru6xxdwu821r">
              {t.gov.whatTitle}
            </h2>
            <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 mb-6 leading-relaxed" data-id="element-318" data-mp="1n1dtkr5a6vq7">
              {t.gov.whatDesc1}
            </p>
            <p className="text-lg text-brand-charcoal/70 dark:text-gray-300 leading-relaxed" data-id="element-319" data-mp="ufkwmy6o2bbc">
              {t.gov.whatDesc2}
            </p>
          </FadeIn>
          <FadeIn delay={0.2} data-id="element-320" data-mp="360bor8fb1neh">
            <div className="bg-brand-cream dark:bg-gray-800 rounded-3xl p-10 border border-gray-100 dark:border-gray-700" data-id="element-321" data-mp="3ateibgk30wuo">
              <div className="grid grid-cols-2 gap-5" data-id="element-322" data-mp="2x2rl5styzf0p">
                {[{
                icon: <Users size={28} data-id="element-323" data-mp="313lamqn291vp" />,
                label: t.gov.nodeLabels[0]
              }, {
                icon: <Store size={28} data-id="element-324" data-mp="3oqthve41ul0d" />,
                label: t.gov.nodeLabels[1]
              }, {
                icon: <Landmark size={28} data-id="element-325" data-mp="3fsr8aa692k1h" />,
                label: t.gov.nodeLabels[2]
              }, {
                icon: <Leaf size={28} data-id="element-326" data-mp="3kp7v911pqscz" />,
                label: t.gov.nodeLabels[3]
              }].map((node, i) => <div key={i} className="bg-white dark:bg-gray-900 rounded-2xl p-6 text-center shadow-sm" data-id="element-327" data-mp="29bm9lpv2b8w7" data-mpi={i}>
                    <div className="w-14 h-14 rounded-xl bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light flex items-center justify-center mx-auto mb-3" data-id="element-328" data-mp="2zzux1zmrz5r9">
                      {node.icon}
                    </div>
                    <p className="font-bold text-sm text-brand-charcoal dark:text-white" data-id="element-329" data-mp="2m37yxtj369jr">
                      {node.label}
                    </p>
                  </div>)}
              </div>
              <div className="flex items-center justify-center gap-2 mt-6 text-brand-green-dark dark:text-brand-green-light font-bold text-sm" data-id="element-330" data-mp="3es6husv19fq4">
                <Network size={16} aria-hidden="true" data-id="element-331" data-mp="39xsjqiozkuhr" />
                {t.gov.ecosystemCenter}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ===== Core Modules ===== */}
      <section className="py-24 px-6 bg-brand-cream dark:bg-gray-800 transition-colors duration-300" data-id="element-332" data-mp="1klk4u3fravl">
        <div className="max-w-7xl mx-auto" data-id="element-333" data-mp="8vzxvyxakopu">
          <FadeIn className="text-center mb-16" data-id="element-334" data-mp="8x1ai7tyhj9o">
            <h2 className="text-3xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-335" data-mp="dub5s4410p5f">
              {t.gov.modulesTitle}
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-336" data-mp="nsha0sdb27dg">
            {t.gov.moduleTitles.map((title, i) => {
            const Icon = MODULE_ICONS[i];
            return <FadeIn key={i} delay={i % 3 * 0.1} data-id="element-337" data-mp="wtfdn9ju0cvi" data-mpi={i}>
                  <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 h-full shadow-sm hover:shadow-md transition-shadow" data-id="element-338" data-mp="wrulfqkzp60t">
                    <div className="w-12 h-12 rounded-xl bg-brand-green/10 dark:bg-brand-green/20 text-brand-green-dark dark:text-brand-green-light flex items-center justify-center mb-6" data-id="element-339" data-mp="16ji8lfaik0wh">
                      <Icon size={24} aria-hidden="true" data-id="element-340" data-mp="1fjew3dl62tpy" />
                    </div>
                    <h3 className="text-xl font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-341" data-mp="2comn4gtcuuml">
                      {title}
                    </h3>
                    <p className="text-brand-charcoal/60 dark:text-gray-300 mb-6 leading-relaxed text-sm" data-id="element-342" data-mp="2hnz4wn5ddbib">
                      {t.gov.moduleDescs[i]}
                    </p>
                    <ul className="space-y-2.5" data-id="element-343" data-mp="2reamhchz7j7o">
                      {t.gov.moduleFeatures[i].map((feat, j) => <li key={j} className="flex items-center gap-2.5 text-sm text-brand-charcoal/80 dark:text-gray-200" data-id="element-344" data-mp="2wbsm2ca3sm30" data-mpi={j}>
                          <CheckCircle2 size={16} className="text-brand-green shrink-0" aria-hidden="true" data-id="element-345" data-mp="35djqldswqmva" />
                          {feat}
                        </li>)}
                    </ul>
                  </div>
                </FadeIn>;
          })}
          </div>
        </div>
      </section>

      {/* ===== Use Cases ===== */}
      <section className="py-24 px-6 max-w-7xl mx-auto" data-id="element-346" data-mp="13n2sepov0h4z">
        <FadeIn className="text-center mb-16" data-id="element-347" data-mp="yorlampy8kqg">
          <h2 className="text-3xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-348" data-mp="ypjc7k8lpuae">
            {t.gov.useCasesTitle}
          </h2>
        </FadeIn>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-349" data-mp="1rcducahy4hkp">
          {t.gov.useCases.map((uc, i) => {
          const Icon = USE_CASE_ICONS[i % USE_CASE_ICONS.length];
          return <FadeIn key={i} delay={i % 4 * 0.08} data-id="element-350" data-mp="1hdy4eaunnecs" data-mpi={i}>
                <div className="bg-brand-cream dark:bg-gray-800 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 h-full flex items-start gap-4" data-id="element-351" data-mp="1cg6bbdi9xm70">
                  <div className="w-10 h-10 rounded-lg bg-brand-charcoal dark:bg-gray-950 text-brand-yellow flex items-center justify-center shrink-0" data-id="element-352" data-mp="61nkktxw2skh">
                    <Icon size={18} aria-hidden="true" data-id="element-353" data-mp="2p6ew9vmvlc20" />
                  </div>
                  <p className="font-semibold text-brand-charcoal dark:text-white text-sm leading-snug pt-1.5" data-id="element-354" data-mp="2t8jn6mlxvuky">
                    {uc}
                  </p>
                </div>
              </FadeIn>;
        })}
        </div>
      </section>

      {/* ===== Impact for Cities ===== */}
      <section className="py-24 px-6 bg-brand-charcoal dark:bg-gray-950 text-white transition-colors duration-300" data-id="element-355" data-mp="25iz3u2lvivb5">
        <div className="max-w-7xl mx-auto" data-id="element-356" data-mp="2a9fjzuh2wv17">
          <FadeIn className="text-center mb-16" data-id="element-357" data-mp="2ab8n4zshgx4g">
            <h2 className="text-3xl md:text-5xl font-sans font-extrabold tracking-tight" data-id="element-358" data-mp="1wij330wim29z">
              {t.gov.impactTitle}
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-359" data-mp="21hlvnlvb6g12">
            {[{
            title: t.gov.impactGovTitle,
            items: t.gov.impactGov,
            icon: <Landmark size={24} data-id="element-360" data-mp="1rhv3vry4uf4o" />
          }, {
            title: t.gov.impactCitizensTitle,
            items: t.gov.impactCitizens,
            icon: <Users size={24} data-id="element-361" data-mp="13wkpc4xmb529" />
          }, {
            title: t.gov.impactTerritoriesTitle,
            items: t.gov.impactTerritories,
            icon: <Globe2 size={24} data-id="element-362" data-mp="1cwha3vgvzjs1" />
          }].map((col, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-363" data-mp="1cytwqlxlaibf" data-mpi={i}>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-8 h-full backdrop-blur-sm" data-id="element-364" data-mp="1qv74328et3c6">
                  <div className="w-12 h-12 rounded-xl bg-brand-green/20 text-brand-green-light flex items-center justify-center mb-6" data-id="element-365" data-mp="1mtvlvhrv7ke0">
                    {col.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-6" data-id="element-366" data-mp="1vo0adujj0jqd">{col.title}</h3>
                  <ul className="space-y-3" data-id="element-367" data-mp="2ajznjjlhta2d">
                    {col.items.map((item, j) => <li key={j} className="flex items-center gap-3 text-white/80" data-id="element-368" data-mp="25lelrfqleibt" data-mpi={j}>
                        <CheckCircle2 size={18} className="text-brand-yellow shrink-0" aria-hidden="true" data-id="element-369" data-mp="2y7qm28sagdh" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* ===== Territorial Intelligence Dashboard ===== */}
      <section className="py-24 px-6 max-w-7xl mx-auto" data-id="element-370" data-mp="3l4vs7i4mklee">
        <FadeIn className="text-center mb-16 max-w-3xl mx-auto" data-id="element-371" data-mp="3q375k2u4pe1d">
          <h2 className="text-3xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-372" data-mp="3q1vzfwd7nu73">
            {t.gov.dashboardTitle}
          </h2>
          <p className="text-lg text-brand-charcoal/60 dark:text-gray-300 leading-relaxed" data-id="element-373" data-mp="15c6lrmo8uefz">
            {t.gov.dashboardDesc}
          </p>
        </FadeIn>
        <FadeIn delay={0.2} data-id="element-374" data-mp="2utu2ov25evz1">
          <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-200 dark:border-gray-700 shadow-xl p-8 md:p-10" data-id="element-375" data-mp="2ut26bi0q8u7p">
            <div className="grid md:grid-cols-3 gap-5" data-id="element-376" data-mp="2pvsg405ikeit">
              {t.gov.dashboardMetrics.map((metric, i) => <div key={i} className="bg-brand-cream dark:bg-gray-900 rounded-2xl p-6 border border-gray-100 dark:border-gray-700" data-id="element-377" data-mp="2l1xrcib1x9cv" data-mpi={i}>
                  <div className="flex items-center justify-between mb-3" data-id="element-378" data-mp="2b403vx10nglb">
                    <span className="text-sm font-bold text-brand-charcoal/70 dark:text-gray-300" data-id="element-379" data-mp="272y2panjgugp">
                      {metric}
                    </span>
                    <BarChart3 size={16} className="text-brand-green-dark dark:text-brand-green-light" aria-hidden="true" data-id="element-380" data-mp="1x6v1c70igzut" />
                  </div>
                  <div className="text-3xl font-extrabold text-brand-charcoal dark:text-white mb-3" data-id="element-381" data-mp="3rlntfw26r8i0">
                    0
                  </div>
                  <div className="flex items-end gap-1 h-10" data-id="element-382" data-mp="1ulwzuiyqp1lc">
                    {[40, 65, 30, 80, 55, 70].map((h, k) => <div key={k} className="flex-1 bg-brand-green/20 dark:bg-brand-green/30 rounded-sm" style={{
                  height: `${h}%`
                }} data-id="element-383" data-mp="1fxsflak1gi0g" data-mpi={k}></div>)}
                  </div>
                </div>)}
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ===== ESG & SDG ===== */}
      <section className="py-24 px-6 bg-brand-cream dark:bg-gray-800 transition-colors duration-300" data-id="element-384" data-mp="23mlftj7p1c0v">
        <div className="max-w-7xl mx-auto" data-id="element-385" data-mp="n8bsmsf6ttbm">
          <FadeIn className="text-center mb-16" data-id="element-386" data-mp="n7k6s9be79yj">
            <span className="inline-flex items-center gap-2 text-brand-green-dark dark:text-brand-green-light font-bold tracking-wider uppercase text-sm mb-4" data-id="element-387" data-mp="s7exabr99xj2">
              <ShieldCheck size={16} aria-hidden="true" data-id="element-388" data-mp="36dmjx46cqp40" />
              ESG
            </span>
            <h2 className="text-3xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-389" data-mp="31fbed5n3b0rl">
              {t.gov.sdgTitle}
            </h2>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5" data-id="element-390" data-mp="3bflpmkpkdy44">
            {t.gov.sdgs.map((sdg, i) => <FadeIn key={i} delay={i * 0.08} data-id="element-391" data-mp="2np7swgw1bb37" data-mpi={i}>
                <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-100 dark:border-gray-700 h-full flex flex-col items-center text-center" data-id="element-392" data-mp="2ipvac8c74q6f">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 text-white font-extrabold text-lg" style={{
                backgroundColor: SDG_COLORS[i]
              }} data-id="element-393" data-mp="2wj4h018rexl9">
                    {sdg.match(/\d+/)?.[0]}
                  </div>
                  <p className="text-sm font-semibold text-brand-charcoal dark:text-white leading-snug" data-id="element-394" data-mp="2oufv9bcfriir">
                    {sdg.replace(/^(ODS|SDG)\s\d+\s—\s/, '')}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* ===== Ecosystem Architecture ===== */}
      <section className="py-24 px-6 max-w-7xl mx-auto" data-id="element-395" data-mp="32v8ta61y4vqm">
        <FadeIn className="text-center mb-16 max-w-3xl mx-auto" data-id="element-396" data-mp="37tadiub3z13o">
          <h2 className="text-3xl md:text-5xl font-sans font-extrabold text-brand-charcoal dark:text-white mb-6 tracking-tight" data-id="element-397" data-mp="37qxwcjd6curo">
            {t.gov.ecosystemTitle}
          </h2>
          <p className="text-lg text-brand-charcoal/60 dark:text-gray-300 leading-relaxed" data-id="element-398" data-mp="3hjdebgoh1jck">
            {t.gov.ecosystemDesc}
          </p>
        </FadeIn>
        <FadeIn delay={0.2} data-id="element-399" data-mp="3qk1hthg8o1b7">
          <div className="relative bg-brand-charcoal dark:bg-gray-950 rounded-3xl p-10 md:p-16" data-id="element-400" data-mp="3qkteywphi73l">
            <div className="flex justify-center mb-10" data-id="element-401" data-mp="3viuwveb6c0dh">
              <div className="bg-brand-green text-white rounded-2xl px-8 py-6 text-center shadow-lg" data-id="element-402" data-mp="9gm40ulyf8sg">
                <Network size={28} className="mx-auto mb-2 text-brand-yellow" aria-hidden="true" data-id="element-403" data-mp="4h9m8o9xwrwq" />
                <p className="font-extrabold text-lg" data-id="element-404" data-mp="3vpx6t20cwb38">
                  {t.gov.ecosystemCenter}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5" data-id="element-405" data-mp="s6c15od6w6nj">
              {t.gov.ecosystemModules.map((mod, i) => {
              const Icon = ECOSYSTEM_ICONS[i % ECOSYSTEM_ICONS.length];
              return <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 text-center text-white" data-id="element-406" data-mp="ifr6gc0mzm9w" data-mpi={i}>
                    <Icon size={22} className="mx-auto mb-2 text-brand-green-light" aria-hidden="true" data-id="element-407" data-mp="1ayod92tnamav" />
                    <p className="font-bold text-sm" data-id="element-408" data-mp="2wt1t1jceckks">{mod}</p>
                  </div>;
            })}
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ===== Investors & Market ===== */}
      <section className="py-24 px-6 bg-brand-charcoal dark:bg-gray-950 text-white transition-colors duration-300" data-id="element-409" data-mp="2nrkh8d60v51w">
        <div className="max-w-7xl mx-auto" data-id="element-410" data-mp="2iubtzy60oats">
          <FadeIn className="text-center mb-16 max-w-3xl mx-auto" data-id="element-411" data-mp="3gg9axr4r083m">
            <h2 className="text-3xl md:text-5xl font-sans font-extrabold mb-6 tracking-tight" data-id="element-412" data-mp="3bgwnb461ywzz">
              {t.gov.investorsTitle}
            </h2>
            <p className="text-lg text-white/70 leading-relaxed" data-id="element-413" data-mp="36ovfbufx3sfr">
              {t.gov.investorsDesc}
            </p>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12" data-id="element-414" data-mp="31p92hm404e7h">
            <FadeIn data-id="element-415" data-mp="32ea6o198r9i">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 h-full" data-id="element-416" data-mp="32wgkdhmslv5">
                <div className="flex items-center gap-2 text-brand-green-light font-extrabold text-lg mb-6" data-id="element-417" data-mp="3udo4n821b21e">
                  <TrendingUp size={20} aria-hidden="true" data-id="element-418" data-mp="5nvudv0i3hy2" />
                  {t.gov.marketB2cTitle}
                </div>
                <ul className="space-y-3" data-id="element-419" data-mp="fjywj0vcrgl0">
                  {t.gov.marketB2c.map((m, i) => <li key={i} className="flex items-center gap-3 text-white/80" data-id="element-420" data-mp="kiik15plh0fh" data-mpi={i}>
                      <div className="w-1.5 h-1.5 rounded-full bg-brand-yellow" data-id="element-421" data-mp="oju8h7wqffm3"></div>
                      {m}
                    </li>)}
                </ul>
              </div>
            </FadeIn>
            <FadeIn delay={0.1} data-id="element-422" data-mp="yc9s09nogc93">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-8 h-full" data-id="element-423" data-mp="139046lwd2np8">
                <div className="flex items-center gap-2 text-brand-yellow font-extrabold text-lg mb-6" data-id="element-424" data-mp="188cq94ferqqr">
                  <Landmark size={20} aria-hidden="true" data-id="element-425" data-mp="1c9okxod5325t" />
                  {t.gov.marketB2gTitle}
                </div>
                <ul className="space-y-3" data-id="element-426" data-mp="399svt6zuj3ot">
                  {t.gov.marketB2g.map((m, i) => <li key={i} className="flex items-center gap-3 text-white/80" data-id="element-427" data-mp="34cail4esq2b0" data-mpi={i}>
                      <div className="w-1.5 h-1.5 rounded-full bg-brand-green-light" data-id="element-428" data-mp="3i50huajc53q2"></div>
                      {m}
                    </li>)}
                </ul>
              </div>
            </FadeIn>
          </div>
          <FadeIn delay={0.2} data-id="element-429" data-mp="3n2ig96zq80jt">
            <p className="text-center text-lg text-white/60 max-w-3xl mx-auto leading-relaxed flex items-start gap-3 justify-center" data-id="element-430" data-mp="3n17e1blxj0ut">
              <Layers size={20} className="text-brand-green-light shrink-0 mt-1" aria-hidden="true" data-id="element-431" data-mp="4u1fsxzikflg" />
              <span data-id="element-432" data-mp="26oikm87wyx2d">{t.gov.investorsNote}</span>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="py-32 px-6 bg-brand-green-dark text-white text-center relative overflow-hidden" data-id="element-433" data-mp="1si73gaci72ks">
        <div className="absolute top-0 left-0 w-96 h-96 bg-brand-green/30 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/3 pointer-events-none" aria-hidden="true" data-id="element-434" data-mp="1wjkbmr32jhuv"></div>
        <div className="max-w-3xl mx-auto relative z-10" data-id="element-435" data-mp="1nr8n5arim4hj">
          <FadeIn data-id="element-436" data-mp="1npntrogxsrl9">
            <h2 className="text-4xl md:text-6xl font-sans font-extrabold mb-8 tracking-tight" data-id="element-437" data-mp="1np5oy3g7l51q">
              {t.gov.ctaFinalTitle}
            </h2>
            <p className="text-xl text-white/80 mb-12 leading-relaxed" data-id="element-438" data-mp="1dudlwk3uxpzk">
              {t.gov.ctaFinalDesc}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-439" data-mp="zvdzlj3thn6k">
              <Link to="/contato" className="inline-flex items-center justify-center gap-2 bg-brand-yellow hover:bg-brand-yellow-dark text-brand-charcoal px-8 py-4 rounded-lg text-base font-bold transition-all shadow-sm hover:shadow-md group" data-id="element-440" data-mp="14tfia2xbzkhi">
                {t.gov.ctaPrimary}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" data-id="element-441" data-mp="z3mjlui1tmaa" />
              </Link>
              <Link to="/contato" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 px-8 py-4 rounded-lg text-base font-bold transition-all" data-id="element-442" data-mp="p8b59dajyh32">
                {t.gov.ctaSecondary}
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>;
}